import { ApiSuccess } from '../utils/api-response';
import { AxiosResponse } from 'axios';
import { authApi } from '../utils/httpClient';

export type ClientStatus = 'ACTIVE' | 'SUSPENDED' | 'INVITED';

export interface Client {
    _id: string;
    firstName: string;
    lastName: string;
    clientName: string; // company/brand name
    email: string;
    name: string;

    // optional fields if your backend sends them
    isActive?: boolean;
    status?: ClientStatus;
    createdAt?: string;
    updatedAt?: string;

}

export interface ListClientsQuery {
    search?: string;
    status?: ClientStatus;
    isActive?: boolean;
    // page?: number; limit?: number; // reserved for future pagination
}

export interface CreateClientDto {
    firstName: string;
    lastName: string;
    clientName: string;
    email: string;
    subscriptionModel: string;
    planId: string;
}

export interface UpdateClientDto {
    clientName: string;
    email: string;
};

const base = '/clients';
// unwraps both { message, data } and raw bodies
function unwrap<T>(res: AxiosResponse<any>): T {
    const body = res.data as any;
    if (body && typeof body === 'object' && 'data' in body) {
        return body.data as T; // ApiSuccess<T> shape
    }
    return body as T; // raw T shape
}
export class ApiError extends Error {
    status?: number;
    details?: any;
    constructor (message: string, status?: number, details?: any) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.details = details;
    }
}

const toApiError = (err: unknown): ApiError => {
    const e = err as any;
    // Axios error path
    if (e?.isAxiosError) {
        const status = e.response?.status;
        const data = e.response?.data;
        const message =
            typeof data?.message === 'string'
                ? data.message
                : Array.isArray(data?.message)
                    ? data.message.join('\n')
                    : data?.error || e.message || 'Request failed';
        return new ApiError(message, status, data);
    }
    // Non-axios (or thrown payload)
    const status = e?.statusCode ?? e?.status;
    const message =
        typeof e?.message === 'string' ? e.message : 'Request failed';
    return new ApiError(message, status, e);
};
export const ClientsAPI = {
    async create(payload: CreateClientDto): Promise<Client> {
        try {
            const res = await authApi.post<ApiSuccess<Client>>(`${base}/create`, payload);
            return unwrap<Client>(res);
        } catch (err) {
            throw toApiError(err);
        }
    },

    list: (q: ListClientsQuery = {}) =>
        authApi.get<ApiSuccess<Client[]>>(`${base}/getAll`, { params: q }).then(r => r.data.data),

    getById: (id: string) =>
        authApi.get<ApiSuccess<Client>>(`${base}/${id}`).then(r => r.data.data),

    update: (id: string, payload: UpdateClientDto) =>
        authApi.put<ApiSuccess<Client>>(`${base}/update/${id}`, payload).then(r => r.data.data),

    remove: (id: string) =>
        authApi.delete<ApiSuccess>(`${base}/delete/${id}`).then(r => r.data.data),

    async suspend(id: string) {
        try {
            const res = await authApi.patch<ApiSuccess<Client>>(`/clients/suspend/${id}`);
            return unwrap<Client>(res);
        } catch (err) {
            throw toApiError(err);
        }
    },

    async reactivate(id: string) {
        try {
            const res = await authApi.patch<ApiSuccess<Client>>(`/clients/reactivate/${id}`);
            return unwrap<Client>(res);
        } catch (err) {
            throw toApiError(err);
        }
    },

    async sendActivationLink(email: string): Promise<void> {
        try {
            const res = await authApi.post<ApiSuccess<void>>('/auth/activate/send', { email });
            return unwrap<void>(res);
        } catch (err) {
            throw toApiError(err);
        }
    },


};